import { useCallback, useEffect, useState, useMemo } from "react";
import { getAllUserRequests } from "../../services/AllServices";
import {
  capitalizeFirstLetter,
  formatDateTime,
  getRequestType,
  showMessage,
  debounce,
} from "../../utils/helpers";
import localStorageService from "../../services/LocalStorageService";

const useRequestManagementViewController = () => {
  const userId = localStorageService.getItem("user_id") || "";
  const [status, setStatus] = useState("pending");
  const [searchText, setSearchText] = useState("");
  const [requestType, setRequestType] = useState(null);
  const [allData, setAllData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 15;

  const totalPages = useMemo(
    () => Math.ceil(totalCount / pageSize),
    [totalCount, pageSize]
  );

  const fetchData = useCallback(
    debounce(async () => {
      setIsLoading(true);
      try {
        const responseData = await getAllUserRequests(
          userId,
          status,
          currentPage,
          pageSize,
          searchText,
          requestType?.label
        );
        let updatedData =
          responseData?.data?.requests?.map((item) => ({
            ...item,
            id: item?.request_id,
            request_type: getRequestType(item?.request_type),
            approval_status: capitalizeFirstLetter(item?.approval_status),
            created_at: formatDateTime(item?.created_at),
          })) || [];

        setAllData(updatedData);
        setTotalCount(responseData?.data?.total_count);
      } catch (error) {
        showMessage(
          "error",
          error?.response?.data?.message || "Something went wrong"
        );
      } finally {
        setIsLoading(false);
      }
    }, 500),
    [userId, status, currentPage, pageSize, searchText, requestType]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    allData,
    currentPage,
    totalPages,
    requestType,
    searchText,
    setSearchText: (text) => setSearchText(text.trim()),
    setStatus,
    clearSearchQuery: () => setSearchText(""),
    handleSelectedRequestType: (e) =>
      setRequestType({ value: e?.value, label: e?.label }),
    handlePageChange: (page) => {
      if (page <= totalPages && page >= 1) setCurrentPage(page);
    },
    fetchData,
    isLoading,
    status,
  };
};

export default useRequestManagementViewController;
